import { Link } from "react-router-dom";
import GuidePage from "@/components/marketing/GuidePage";
import { BRAND, LAB_REPORT } from "@/content/brand";

/**
 * Pillar page: /cold-pressed-coconut-oil-benefits
 *
 * The flagship guide. It separates what is traditional from what has been
 * studied, and is honest about saturated fat. Every study named here is in
 * docs/content/RESEARCH.md. Rules: docs/content/WRITING_RULES.md.
 * No dashes or hyphens in visible copy, no disease claims.
 */

const SAT_FAT = LAB_REPORT.rows.find((r) => r.param === "Saturated fat")?.value;

const EVIDENCE = [
  ["Cooking at everyday heat", "Mostly saturated fat, which oxidises less when heated than oils high in polyunsaturated fat. One heating study placed coconut oil second of ten oils tested. Less suited to deep frying.", "Moderate"],
  ["What the oil is made of", "About half its fatty acids are lauric acid (Codex range 45.1 to 53.2%). Roughly 90% of its fat is saturated.", "Strong"],
  ["Hair", "In lab tests on hair fibres, coconut oil used before washing reduced protein loss, and it was shown to enter the hair shaft. No trials on hair fall or growth.", "Moderate (lab studies)"],
  ["Dry skin", "In small double blind trials, virgin coconut oil worked as well as mineral oil as a moisturiser.", "Moderate (small trials)"],
  ["Baby massage", "A long Indian custom. Studied in hospital newborn units with no adverse effects reported in one trial. Not a replacement for your paediatrician's advice.", "Traditional, some hospital trials"],
  ["Oil pulling", "An Ayurvedic practice. A few small studies have looked at plaque. It does not replace brushing.", "Traditional, small studies"],
  ["Cholesterol and heart", "Raises LDL cholesterol compared with unsaturated plant oils. Not a heart health food.", "Strong (it raises LDL)"],
];

function Table({ head, rows, caption }) {
  return (
    <div className="overflow-x-auto">
      <table>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>{head.map((h) => <th key={h} scope="col">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[0]}>{r.map((c, i) => (i === 0 ? <th key={i} scope="row">{c}</th> : <td key={i}>{c}</td>))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const sections = [
  {
    id: "what-is-it",
    heading: "What is cold pressed coconut oil?",
    body: (
      <>
        <p>
          Cold pressed coconut oil is oil pressed out of dried coconut mechanically, without added heat, and then left
          as it is. It is not refined, bleached or deodorised. So it keeps the smell and taste of coconut, which is
          exactly what refining is designed to remove.
        </p>
        <p>
          FSSAI defines coconut oil (naryal ka tel) as oil expressed from copra, the dried kernel of the coconut, and
          has a separate standard for virgin coconut oil. Karijeeva is sold as cold pressed coconut oil, because that
          is the accurate name. One ingredient, coconut, with no other oils, colour or aroma added.
        </p>
      </>
    ),
  },
  {
    id: "evidence-at-a-glance",
    heading: "Coconut oil benefits: the evidence at a glance",
    body: (
      <>
        <p>
          Most pages list ten or twenty coconut oil benefits as if they were equal. They are not. Some rest on good
          science, some on a few small studies, and some on generations of Indian household practice.
        </p>
        <Table
          caption="Evidence for common uses of coconut oil"
          head={["Use", "What we know", "Strength of evidence"]}
          rows={EVIDENCE}
        />
        <p className="note">
          Strong means several human trials or a review of them. Moderate means one or a few trials, or careful lab
          work. Traditional means long household use without clinical proof. Coconut oil does not prevent, treat or
          cure any disease, and we will never tell you it does.
        </p>
      </>
    ),
  },
  {
    id: "whats-in-it",
    heading: "What is in cold pressed coconut oil?",
    body: (
      <>
        <p>
          Almost all of it is fat, and most of that fat is saturated. Below is the nutrition panel from our latest
          independent lab report (reference {LAB_REPORT.ref}), per 100 g of oil.
        </p>
        <Table
          caption={`Karijeeva lab report ${LAB_REPORT.ref}, per 100 g`}
          head={["Parameter", "Per 100 g", "Test method"]}
          rows={LAB_REPORT.rows.map((r) => [r.param, `${r.value} ${r.unit}`, r.method])}
        />
        <p>
          {SAT_FAT} g of every 100 g is saturated fat. That is normal for coconut oil, and it is why the oil sets white
          in the cold. The zero for cholesterol is true of every plant oil, so it is nothing special.
        </p>
        <p>
          The main saturated fat is lauric acid. The Codex international standard puts it at 45.1 to 53.2% of the
          fatty acids in coconut oil. Coconut oil is not the same as the MCT oil sold in tins, which is mostly shorter
          fatty acids. Our current report lists fat types, not individual fatty acids, so we quote the Codex range
          rather than a number of our own. More on what it is and what studies found in{" "}
          <Link to="/lauric-acid-in-coconut-oil">lauric acid in Karijeeva coconut oil</Link>.
        </p>
        <p>
          You can read the full report, with every batch we have tested, on our <Link to="/lab-reports">lab reports page</Link>.
        </p>
      </>
    ),
  },
  {
    id: "kitchen",
    heading: "Is cold pressed coconut oil good for cooking?",
    body: (
      <>
        <p>
          For the dishes it belongs in, yes. The biggest benefit in the kitchen is the one you can smell. A spoon of
          cold pressed oil in a hot kadai gives a thoran, poriyal, avial or coconut chutney tadka the aroma those
          recipes were built around. Refined oil cannot do that, because the aroma has been stripped out.
        </p>
        <p>
          It also holds up well at everyday cooking heat. Being mostly saturated fat, it oxidises less than oils rich
          in polyunsaturated fat. In one study that heated ten oils at 180°C and above, coconut oil formed the second
          fewest polar compounds, after extra virgin olive oil. That study was small and industry linked, so treat it
          as a pointer, not proof.
        </p>
        <p>
          The limits: other research found coconut oil gives off more fumes once pushed past its smoke point and is
          less suited to deep frying. Published smoke point figures vary, and we have not measured ours, so we quote
          no number.
        </p>
        <h3>How to cook with it well</h3>
        <ul>
          <li>Use it for tempering, shallow frying, sautéing, dosa and appam, upma, and baking.</li>
          <li>Cook at medium heat. If the oil starts to smoke, turn the flame down.</li>
          <li>Do not reheat or reuse oil you have fried in. ICMR NIN advises against it for any oil.</li>
        </ul>
        <p>
          More in our <Link to="/cooking-with-coconut-oil">guide to cooking with coconut oil</Link>.
        </p>
      </>
    ),
  },
  {
    id: "hair",
    heading: "What are the benefits of coconut oil for hair?",
    body: (
      <>
        <p>
          The Sunday champi is one of the oldest habits in Indian homes, and here the science is kinder than usual.
          A 2001 study that imaged hair fibres showed coconut oil entering the hair shaft, while mineral oil did not.
          In a 2003 lab study, coconut oil was the only one of three oils tested that significantly reduced protein
          loss from hair.
        </p>
        <p>
          Two caveats. These were lab studies on hair fibres, not trials on people, and the 2003 authors worked for a
          large coconut oil company. There is no good evidence that coconut oil stops hair fall or regrows hair.
        </p>
        <p>
          The practical takeaway: warm a spoon or two, massage it into the scalp and lengths, and leave it for an hour
          or overnight before washing. Our <Link to="/coconut-oil-for-hair">coconut oil for hair guide</Link> covers
          amounts for different hair types.
        </p>
      </>
    ),
  },
  {
    id: "skin",
    heading: "Is coconut oil good for skin?",
    body: (
      <>
        <p>
          As a simple moisturiser for dry skin, the evidence is reasonable. In a 2004 double blind trial with 34
          people, extra virgin coconut oil was as effective and safe as mineral oil for dry skin. A 2014 trial in 117
          children with mild to moderate atopic dermatitis found virgin coconut oil did better than mineral oil as a
          moisturiser over 8 weeks.
        </p>
        <p>
          Both trials used virgin coconut oil, a slightly different product from ours, and neither makes coconut oil a
          treatment for eczema or any skin condition. For that, follow your dermatologist.
        </p>
        <p>
          Where it shines is everyday dryness: a few drops on elbows, heels and lips after a bath, while the skin is
          still damp. On the face, go slowly, as some find it too heavy. More in
          our <Link to="/coconut-oil-for-skin">coconut oil for skin guide</Link>.
        </p>
      </>
    ),
  },
  {
    id: "babies",
    heading: "Is coconut oil safe for baby massage?",
    body: (
      <>
        <p>
          Daily oil massage for babies is a strong Indian tradition, and coconut oil is a favourite in South Indian
          homes. It has also been studied: an Indian trial compared it with mineral oil in newborns, and a 2018 trial
          in babies born before 30 weeks found better skin condition scores with no adverse effects. These were
          supervised hospital studies.
        </p>
        <p>
          For a baby, what is in the jar matters most. A single ingredient oil with no added fragrance, colour or
          other oils is the sensible choice. Patch test first, and speak to your paediatrician before you start,
          especially for a preterm baby or one with sensitive skin. See the full <Link to="/coconut-oil-for-baby-massage">baby massage guide</Link>.
        </p>
      </>
    ),
  },
  {
    id: "oil-pulling",
    heading: "Does oil pulling with coconut oil work?",
    body: (
      <>
        <p>
          Oil pulling, or gandusha, means swishing a spoon of oil around the mouth for about ten minutes and then
          spitting it out. It is an Ayurvedic habit, and most older studies used sesame oil. A small 2015 study linked
          coconut oil pulling with lower plaque and gum inflammation scores, and a 2020 systematic review found some
          effect on plaque. The evidence is modest.
        </p>
        <p>
          Spit the oil into the bin rather than the sink, since it can set in cold pipes, and never treat it as a
          replacement for brushing, flossing or your dentist. Steps are in
          our <Link to="/oil-pulling-with-coconut-oil">oil pulling guide</Link>.
        </p>
      </>
    ),
  },
  {
    id: "saturated-fat",
    heading: "Is coconut oil bad for cholesterol? The honest answer",
    body: (
      <>
        <p>
          This is the part many pages skip. Coconut oil raises LDL cholesterol compared with unsaturated
          plant oils. A 2020 meta analysis of 16 trials in the journal Circulation found that, compared with non
          tropical vegetable oils, coconut oil raised LDL cholesterol by 10.47 mg/dL and HDL by 4.00 mg/dL, with no
          significant effect on weight, blood sugar or triglycerides. The authors concluded it should not be viewed as
          a healthy oil for reducing cardiovascular risk.
        </p>
        <p>
          The American Heart Association advised against coconut oil in 2017 for the same reason. A 2016 review found
          it raised LDL less than butter, but more than unsaturated plant oils. So claims that coconut oil is good for
          the heart or burns fat are not supported, and you will not find them on our labels.
        </p>
        <h3>So how should you use it?</h3>
        <p>
          ICMR NIN, India's national nutrition institute, advises a combination of oils, blended or in rotation, rather
          than any single oil. Its 2024 guidelines suggest about 20 to 30 g of visible fat a day for most sedentary
          adults, counting all oil, ghee and butter together.
        </p>
        <ul>
          <li>Keep coconut oil for the dishes it suits: Kerala and Karnataka coastal cooking, chutney tadkas, appam, baking.</li>
          <li>Use other oils in your rotation for the rest, such as groundnut, mustard, sesame or rice bran.</li>
          <li>Measure with a spoon instead of pouring from the jar.</li>
          <li>If your doctor has asked you to watch your cholesterol, follow their advice on saturated fat.</li>
        </ul>
        <p>
          We are not asking you to cook everything in coconut oil. We are asking that the coconut oil you already use
          be the real thing.
        </p>
      </>
    ),
  },
  {
    id: "why-cold-pressed",
    heading: "Why choose cold pressed instead of refined coconut oil?",
    body: (
      <>
        <p>
          Refined coconut oil, often labelled RBD, is refined, bleached and deodorised, which removes its colour, smell
          and taste. Lab work comparing virgin coconut oil with RBD oil found more natural phenolic compounds in the
          unrefined oil (Marina and colleagues, 2009). That study used virgin oil, so we do not claim its numbers for
          ours, and we make no health claim from it.
        </p>
        <p>
          What cold pressed gives you for certain is flavour, aroma, and nothing done to the oil after pressing. The
          full comparison is in our guide to <Link to="/cold-pressed-vs-refined-coconut-oil">cold pressed vs refined coconut oil</Link>.
        </p>
      </>
    ),
  },
  {
    id: "how-to-choose",
    heading: "How do you choose a good cold pressed coconut oil?",
    body: (
      <>
        <p>
          Adulterated coconut oil is a real problem in India. In 2018, Kerala's Commissioner of Food Safety banned 45
          coconut oil brands in May, 51 in June and 74 in December, the main problem being cheaper oils mixed in.
        </p>
        <ol>
          <li><strong>Read the label.</strong> It should list one ingredient, coconut oil, and say whether it is refined or cold pressed. Be wary of words like “cholesterol friendly” or “soothing to heart”, which FSSAI bars on edible oil labels.</li>
          <li><strong>Check the FSSAI licence.</strong> Packaged food must carry one, and you can look it up on the FSSAI website. Ours is {BRAND.fssai}.</li>
          <li><strong>Ask for a lab report.</strong> A brand that tests its oil should show you. We publish ours.</li>
          <li><strong>Do the fridge test.</strong> FSSAI's home test: keep a little oil in a glass in the refrigerator for 30 minutes. Pure coconut oil sets solid, while mixed in liquid oils stay as a separate layer. It will not catch every adulterant.</li>
          <li><strong>Smell it.</strong> Cold pressed oil should smell of coconut, not of nothing, and never rancid.</li>
        </ol>
        <p>
          Try it on any coconut oil, ours included. Steps are in <Link to="/how-to-check-coconut-oil-purity">how to check coconut oil purity</Link>.
        </p>
      </>
    ),
  },
  {
    id: "who-should-be-careful",
    heading: "Who should be careful with coconut oil?",
    body: (
      <>
        <ul>
          <li><strong>People allergic to coconut.</strong> Do not eat it or put it on your skin.</li>
          <li><strong>Anyone with sensitive skin or a skin condition.</strong> Patch test on the inner arm and wait a day before wider use. Stop if you see redness or itching.</li>
          <li><strong>Babies, especially preterm ones.</strong> Speak to your paediatrician first, and patch test.</li>
          <li><strong>People told to limit saturated fat.</strong> Follow your doctor's advice and keep coconut oil to small amounts within a mix of oils.</li>
        </ul>
      </>
    ),
  },
  {
    id: "why-karijeeva",
    heading: "Why Karijeeva",
    body: (
      <>
        <p>
          We grow the coconuts ourselves, most of them in Tiptur and the rest on our coastal farms between Gokarna and
          Shirali. We press them in small batches, never refine, and send every batch to an independent food testing lab before
          it is packed. We have tested {BRAND.batchesTested} batches so far, and some
          batches sell out.
        </p>
        <p>
          If you already use coconut oil for cooking, champi or massage, switching to one you can check is a simple
          change. <Link to="/products">See the oil and sizes</Link>, or look at
          the <Link to="/lab-reports">lab reports</Link> first. Delivery is free across India above ₹998.
        </p>
      </>
    ),
  },
];

const faqs = [
  {
    q: "Is cold pressed coconut oil good for health?",
    a: "It is a pure, unrefined cooking fat that keeps the coconut aroma and taste refining removes. It is also high in saturated fat and raises LDL cholesterol compared with unsaturated oils, so it is not a health food. Use it in sensible amounts as one of the oils in your kitchen, as ICMR NIN advises.",
  },
  {
    q: "What are the benefits of coconut oil for hair?",
    a: "Lab studies on hair fibres found that coconut oil enters the hair shaft and, used before washing, reduced protein loss more than sunflower or mineral oil. That makes it a good choice for a pre wash champi. There is no good evidence that it stops hair fall or regrows hair.",
  },
  {
    q: "Can we use coconut oil for cooking daily?",
    a: "Yes, in sensible amounts and alongside other oils. ICMR NIN suggests using a combination of oils rather than any single oil, and about 20 to 30 g of visible fat a day for most sedentary adults. Use coconut oil for the dishes it suits and other oils for the rest.",
  },
  {
    q: "Is coconut oil good for the heart?",
    a: "No, and we do not claim it is. A 2020 meta analysis in Circulation found coconut oil raised LDL cholesterol by 10.47 mg/dL compared with non tropical vegetable oils, and the American Heart Association advises against it. If you have a heart condition or high cholesterol, follow your doctor's advice.",
  },
  {
    q: "What is the difference between cold pressed and virgin coconut oil?",
    a: "FSSAI has separate standards for coconut oil, which is pressed from copra, and virgin coconut oil. Cold pressed means the oil is pressed without added heat and not refined. Karijeeva is sold as cold pressed coconut oil, which is the accurate name for it.",
  },
  {
    q: "Can I use cold pressed coconut oil for deep frying?",
    a: "It is better suited to tempering, sautéing and shallow frying at medium heat. Research on heated oils suggests coconut oil is less suited to deep frying, and published smoke point figures vary. Whatever oil you fry in, do not reheat or reuse it.",
  },
  {
    q: "Can the same coconut oil be used for cooking and hair?",
    a: "A pure, food grade cold pressed coconut oil can be used for both, which is how many Indian homes have always used it. Keep a separate small jar for hair so you are not dipping wet or oily fingers into your cooking oil.",
  },
  {
    q: "How can I tell if coconut oil is pure?",
    a: "Check for one ingredient on the label, an FSSAI licence number and a lab report. At home, try FSSAI's fridge test: keep a little oil in a glass in the refrigerator for 30 minutes. Pure coconut oil sets solid, while mixed in liquid oils stay as a separate layer.",
  },
];

const sources = [
  { label: "Codex Alimentarius, Standard for Named Vegetable Oils (CXS 210), lauric acid range", url: "https://www.fao.org/4/y2774e/y2774e04.htm" },
  { label: "FSSAI, Food Products Standards Chapter 2.2, fats and oils (coconut oil and virgin coconut oil)", url: "https://www.fssai.gov.in/upload/uploadfiles/files/Chapter%202_2_Fats_oils%20and%20fat%20emulsions.pdf" },
  { label: "Neelakantan, Seah and van Dam 2020, Circulation, coconut oil and blood lipids", url: "https://www.ahajournals.org/doi/10.1161/CIRCULATIONAHA.119.043052" },
  { label: "Sacks and colleagues 2017, American Heart Association Presidential Advisory on dietary fats", url: "https://www.ahajournals.org/doi/abs/10.1161/cir.0000000000000510" },
  { label: "Eyres and colleagues 2016, Nutrition Reviews, coconut oil and cardiovascular risk factors", url: "https://pubmed.ncbi.nlm.nih.gov/26946252/" },
  { label: "ICMR NIN, frequently asked questions (combination of oils)", url: "https://www.nin.res.in/faqs.html" },
  { label: "ICMR NIN, Dietary Guidelines for Indians 2024", url: "https://www.nin.res.in/dietaryguidelines/pdfjs/locale/DGI_2024.pdf" },
  { label: "De Alzaa, Guillaume and Ravetti 2018, Acta Scientific Nutritional Health, oils under heat", url: "https://actascientific.com/ASNH/pdf/ASNH-02-0083.pdf" },
  { label: "Emissions of volatile aldehydes from heated cooking oils, Food Chemistry", url: "https://www.sciencedirect.com/science/article/abs/pii/S0308814609011303" },
  { label: "Marina and colleagues 2009, Int J Food Sci Nutr, virgin and refined coconut oil", url: "https://www.tandfonline.com/doi/full/10.1080/09637480802549127" },
  { label: "Rele and Mohile 2003, J Cosmet Sci, oils and hair protein loss", url: "https://www.researchgate.net/publication/10787534_Effect_of_mineral_oil_sunflower_oil_and_coconut_oil_on_prevention_of_hair_damage" },
  { label: "Ruetsch and colleagues 2001, J Cosmet Sci, oil penetration into hair", url: "https://pubmed.ncbi.nlm.nih.gov/11413497/" },
  { label: "Agero and Verallo Rowell 2004, Dermatitis, coconut oil for dry skin", url: "https://journals.sagepub.com/doi/abs/10.2310/derm.1.2004.2015" },
  { label: "Evangelista and colleagues 2014, Int J Dermatol, virgin coconut oil in children", url: "https://pubmed.ncbi.nlm.nih.gov/24320105/" },
  { label: "Sankaranarayanan and colleagues 2005, Indian Pediatrics, oil massage in newborns", url: "https://pubmed.ncbi.nlm.nih.gov/16208048/" },
  { label: "Strunk and colleagues 2018, Neonatology, coconut oil for preterm skin", url: "https://pubmed.ncbi.nlm.nih.gov/29197867/" },
  { label: "Peedikayil and colleagues 2015, Niger Med J, coconut oil pulling", url: "https://dx.doi.org/10.4103/0300-1652.153406" },
  { label: "Woolley and colleagues 2020, Heliyon, oil pulling systematic review", url: "https://www.sciencedirect.com/science/article/pii/S2405844020316327" },
  { label: "Onmanorama, Kerala bans 74 coconut oil brands (December 2018)", url: "https://www.onmanorama.com/news/kerala/2018/12/18/adulteration-74-coconut-oil-brands-banned-in-kerala.html" },
  { label: "FSSAI Eat Right India, DART home tests for adulteration", url: "https://eatrightindia.gov.in/dart/" },
  { label: "FSSAI, labelling and display compendium (words barred on edible oil labels)", url: "https://fssai.gov.in/upload/uploadfiles/files/Comp_Labelling.pdf" },
  { label: "CEBM Oxford, commentary on the Gbinigie 2016 oil pulling review", url: "https://www.cebm.ox.ac.uk/news/views/oil-pulling-does-it-work-anecdotally-yes-but-the-jury-is-still-out-in-this-review" },
];

const related = [
  { label: "What the studies say", to: "/coconut-oil-cooking-studies" },
  { label: "Coconut oil in Ayurveda", to: "/ayurvedic-benefits-of-coconut-oil" },
  { label: "Coconut oil vs other oils", to: "/coconut-oil-vs-other-cooking-oils" },
  { label: "Cold pressed vs refined", to: "/cold-pressed-vs-refined-coconut-oil" },
  { label: "Check coconut oil purity", to: "/how-to-check-coconut-oil-purity" },
  { label: "Cooking with coconut oil", to: "/cooking-with-coconut-oil" },
  { label: "Coconut oil for hair", to: "/coconut-oil-for-hair" },
  { label: "Coconut oil for skin", to: "/coconut-oil-for-skin" },
  { label: "Baby massage", to: "/coconut-oil-for-baby-massage" },
  { label: "Oil pulling", to: "/oil-pulling-with-coconut-oil" },
  { label: "Lab reports", to: "/lab-reports" },
];

export default function PillarPage() {
  return (
    <GuidePage
      path="/cold-pressed-coconut-oil-benefits"
      seoTitle="Cold pressed coconut oil benefits: what's traditional, what's proven"
      seoDescription="An honest guide to cold pressed coconut oil: what is in it, what the studies say for cooking, hair, skin, baby massage and oil pulling, and the truth about saturated fat."
      eyebrow="Learn"
      title="Cold pressed coconut oil benefits: what's traditional, what's proven"
      tldr={`Cold pressed coconut oil is unrefined oil pressed without added heat, so it keeps the coconut aroma and taste that suit South Indian cooking. It holds up well to everyday tempering, lab studies support it as a pre wash hair oil, small trials support it as a simple skin moisturiser, and baby massage and oil pulling rest mostly on tradition. It is also about 90% saturated fat and raises LDL cholesterol compared with unsaturated oils, so use it as one oil in a varied kitchen, not as a health food.`}
      updated="2026-09-24"
      sections={sections}
      faqs={faqs}
      sources={sources}
      related={related}
      testid="pillar-page"
    />
  );
}
